import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { format, formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { publicApi } from "../../api/http";
import { authAxios, authRequestHeaders, useAuth } from "../../contexts/AuthContext";
import { userDisplayName, userInitial } from "../../utils/userDisplayName";
import {
    ALL_TRAIT_KEYS,
    TRAIT_SCORE_MAX,
    TRAIT_SCORE_MIN,
    emptyTraitScores,
    isGoalie,
    traitFieldsForPlayer,
} from "../CreateRanking/rankingTraitFields";
import "../../components/SearchBar/SearchBar.css";
import "./PlayerScoutingReportTab.css";

function parseApiUtcDate(dateStr) {
    if (!dateStr) return null;
    const trimmed = dateStr.trim();
    if (/Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(trimmed)) {
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const iso = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
    const parsed = new Date(`${iso}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatReportDate(dateStr) {
    const date = parseApiUtcDate(dateStr);
    if (!date) return dateStr || "";

    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return "just now";

    const daysAgo = diffMs / (1000 * 60 * 60 * 24);
    if (daysAgo < 7) {
        return formatDistanceToNow(date, { addSuffix: true });
    }
    return format(date, "MMM d, yyyy");
}

function emptyForm(playerId) {
    return {
        player_id: playerId,
        public: false,
        report_confidence_level: "",
        floor: "",
        ceiling: "",
        nhl_eta: "",
        game_watched: "",
        comparable_player: "",
        weakness: "",
        strength: "",
        summary: "",
        ...emptyTraitScores(),
    };
}

function formFromReport(report) {
    const traits = Object.fromEntries(
        ALL_TRAIT_KEYS.map((key) => [
            key,
            report[key] != null && report[key] !== "" ? String(report[key]) : "",
        ]),
    );
    return {
        player_id: report.player_id,
        public: Boolean(report.public),
        report_confidence_level: normalizeConfidence(report.report_confidence_level),
        floor: normalizeProjectionTier(report.floor) || String(report.floor || "").trim(),
        ceiling: normalizeProjectionTier(report.ceiling) || String(report.ceiling || "").trim(),
        nhl_eta: normalizeNhlEta(report.nhl_eta) || String(report.nhl_eta || "").trim(),
        game_watched: report.game_watched || report.games_watched || "",
        comparable_player: report.comparable_player || "",
        weakness: report.weakness || "",
        strength: report.strength || "",
        summary: report.summary || "",
        ...traits,
    };
}

function buildPayload(form) {
    const payload = {
        player_id: form.player_id,
        public: form.public,
        report_confidence_level: form.report_confidence_level.trim() || null,
        floor: form.floor.trim() || null,
        ceiling: form.ceiling.trim() || null,
        nhl_eta: form.nhl_eta.trim() || null,
        game_watched: form.game_watched.trim() || null,
        comparable_player: form.comparable_player.trim() || null,
        weakness: form.weakness.trim() || null,
        strength: form.strength.trim() || null,
        summary: form.summary.trim() || null,
    };

    ALL_TRAIT_KEYS.forEach((key) => {
        const raw = form[key];
        if (raw === "" || raw == null) {
            payload[key] = null;
            return;
        }
        payload[key] = Number(raw);
    });

    return payload;
}

const CONFIDENCE_OPTIONS = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
];

const NHL_ETA_SEASONS = Array.from({ length: 11 }, (_, index) => {
    const startYear = 2026 + index;
    return `${startYear}-${startYear + 1}`;
});

const PROJECTION_TIERS = [
    "1st line - NHL",
    "2nd line - NHL",
    "3rd line - NHL",
    "4th line - NHL",
    "1st line - AHL",
    "2nd line - AHL",
    "3rd line - AHL",
    "4th line - AHL",
];

function normalizeConfidence(value) {
    if (!value) return "";
    const normalized = String(value).trim().toLowerCase();
    return CONFIDENCE_OPTIONS.some((option) => option.value === normalized)
        ? normalized
        : "";
}

function formatConfidenceLabel(value) {
    const normalized = normalizeConfidence(value);
    if (!normalized) return value || "";
    return CONFIDENCE_OPTIONS.find((option) => option.value === normalized)?.label || value;
}

function normalizeNhlEta(value) {
    if (!value) return "";
    const trimmed = String(value).trim();
    return NHL_ETA_SEASONS.includes(trimmed) ? trimmed : "";
}

function nhlEtaOptions(currentValue) {
    const trimmed = String(currentValue || "").trim();
    if (trimmed && !NHL_ETA_SEASONS.includes(trimmed)) {
        return [trimmed, ...NHL_ETA_SEASONS];
    }
    return NHL_ETA_SEASONS;
}

function projectionTierIndex(value) {
    const trimmed = String(value || "").trim();
    return PROJECTION_TIERS.indexOf(trimmed);
}

function normalizeProjectionTier(value) {
    const trimmed = String(value || "").trim();
    return PROJECTION_TIERS.includes(trimmed) ? trimmed : "";
}

function projectionTierOptions(currentValue, { minIndex = 0, maxIndex = PROJECTION_TIERS.length - 1 } = {}) {
    const options = PROJECTION_TIERS.filter(
        (_, index) => index >= minIndex && index <= maxIndex,
    );
    const trimmed = String(currentValue || "").trim();
    if (trimmed && !options.includes(trimmed)) {
        return [trimmed, ...options];
    }
    return options;
}

function projectionTiersValid(floor, ceiling) {
    const floorIdx = projectionTierIndex(floor);
    const ceilingIdx = projectionTierIndex(ceiling);
    if (floorIdx === -1 || ceilingIdx === -1) return true;
    return floorIdx >= ceilingIdx;
}

const selectLabelProps = {
    shrink: true,
    sx: {
        fontSize: "0.68rem",
        lineHeight: 1.2,
        fontWeight: 500,
    },
};

const selectFieldSx = {
    fontSize: "0.82rem",
    "& .MuiOutlinedInput-notchedOutline legend": {
        fontSize: "0.68rem",
        lineHeight: 1.2,
    },
    "& .MuiOutlinedInput-notchedOutline legend span": {
        paddingInline: "6px",
    },
};

const traitLabelProps = {
    shrink: true,
    sx: {
        fontSize: "0.62rem",
        lineHeight: 1.2,
        fontWeight: 500,
    },
};

const traitFieldSx = {
    fontSize: "0.82rem",
    "& .MuiOutlinedInput-notchedOutline legend": {
        fontSize: "0.62rem",
        lineHeight: 1.2,
    },
    "& .MuiOutlinedInput-notchedOutline legend span": {
        paddingInline: "5px",
    },
};

function renderSelectValue(value, formatValue = (item) => item) {
    if (!value) {
        return <span className="scouting-report-select-placeholder">Not set</span>;
    }
    return formatValue(value);
}

function sortPlayerOptions(list) {
    return [...list].sort((a, b) =>
        `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`),
    );
}

function playerDisplayName(player) {
    return [player?.first_name, player?.last_name].filter(Boolean).join(" ").trim();
}

function traitColorClass(value) {
    const n = Number(value);
    if (Number.isNaN(n)) return "scouting-report-trait-bar--na";
    if (n <= 1) return "scouting-report-trait-bar--low";
    if (n === 2) return "scouting-report-trait-bar--midlow";
    if (n === 3) return "scouting-report-trait-bar--mid";
    if (n === 4) return "scouting-report-trait-bar--midhigh";
    return "scouting-report-trait-bar--high";
}

const selectMenuProps = {
    PaperProps: {
        sx: {
            backgroundColor: "var(--bg-surface-2)",
            border: "1px solid var(--border-default)",
            borderRadius: "10px",
            "& .MuiMenuItem-root": {
                fontSize: "0.84rem",
                color: "var(--text-primary)",
                "&.Mui-selected": {
                    backgroundColor: "rgba(126, 168, 98, 0.15)",
                    color: "var(--brand-strong)",
                },
                "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                },
            },
        },
    },
};

const SCOUTING_VIEWS = {
    PUBLIC: "public",
    YOURS: "yours",
    CREATE: "create",
};

const scoutingViewToggleSx = {
    flexShrink: 0,
    "& .MuiToggleButtonGroup-grouped": {
        margin: 0,
    },
    "& .MuiToggleButton-root": {
        color: "var(--text-muted)",
        borderColor: "var(--border-default)",
        fontSize: "0.68rem",
        fontWeight: 600,
        textTransform: "none",
        padding: "3px 8px",
        gap: "4px",
        minHeight: 28,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        "& .MuiSvgIcon-root": {
            fontSize: "0.85rem",
        },
        "&.Mui-selected": {
            color: "var(--text-primary)",
            backgroundColor: "rgba(126, 168, 98, 0.18)",
            borderColor: "var(--brand-strong)",
            "&:hover": {
                backgroundColor: "rgba(126, 168, 98, 0.24)",
            },
        },
    },
};

function PlayerScoutingReportTab({ playerId, playerData, isActive, onReportCountChange }) {
    const { user, loading: authLoading, ensureAccessToken } = useAuth();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [form, setForm] = useState(() => emptyForm(playerId));
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [actionId, setActionId] = useState(null);
    const [comparablePlayerInput, setComparablePlayerInput] = useState("");
    const [comparablePlayerOptions, setComparablePlayerOptions] = useState([]);
    const [comparablePlayerCache, setComparablePlayerCache] = useState([]);
    const [activeView, setActiveView] = useState(SCOUTING_VIEWS.PUBLIC);
    const [pendingDeleteReport, setPendingDeleteReport] = useState(null);

    const traitFields = useMemo(
        () => traitFieldsForPlayer(playerData || {}),
        [playerData],
    );

    const ceilingTierIndex = projectionTierIndex(form.ceiling);
    const floorTierIndex = projectionTierIndex(form.floor);

    const ceilingOptions = useMemo(
        () => projectionTierOptions(form.ceiling, {
            minIndex: 0,
            maxIndex: floorTierIndex === -1 ? PROJECTION_TIERS.length - 1 : floorTierIndex,
        }),
        [form.ceiling, floorTierIndex],
    );

    const floorOptions = useMemo(
        () => projectionTierOptions(form.floor, {
            minIndex: ceilingTierIndex === -1 ? 0 : ceilingTierIndex,
            maxIndex: PROJECTION_TIERS.length - 1,
        }),
        [form.floor, ceilingTierIndex],
    );

    const publicReports = useMemo(
        () => reports.filter((report) => report.public),
        [reports],
    );

    const yourReports = useMemo(
        () => reports.filter((report) => report.is_owner),
        [reports],
    );

    const listReports = activeView === SCOUTING_VIEWS.PUBLIC ? publicReports : yourReports;

    const listEmptyState = useMemo(() => {
        if (activeView === SCOUTING_VIEWS.PUBLIC) {
            return {
                title: "No public scouting reports yet",
                caption: user
                    ? "Be the first to publish your scouting take on this player."
                    : "Sign in to write the first scouting report.",
            };
        }
        return {
            title: "You haven't posted a scouting report yet",
            caption: "Switch to Create to write your first report for this player.",
        };
    }, [activeView, user]);

    const resetForm = useCallback(() => {
        setForm(emptyForm(playerId));
        setEditingId(null);
        setSubmitError("");
        setComparablePlayerInput("");
        setComparablePlayerOptions([]);
        setComparablePlayerCache([]);
    }, [playerId]);

    const fetchComparablePlayers = async (value) => {
        try {
            const response = await publicApi.get(`/players/search/${value}`);
            const filtered = (response.data || []).filter((p) => p.player_id !== playerId);
            const sorted = sortPlayerOptions(filtered);
            setComparablePlayerCache(sorted);
            setComparablePlayerOptions(sorted);
        } catch (err) {
            console.warn("[scouting-reports] comparable player search failed", err);
        }
    };

    const handleComparablePlayerSearchChange = (event) => {
        const value = event.target.value;
        setComparablePlayerInput(value);
        setForm((prev) => ({ ...prev, comparable_player: value }));

        if (value.length === 3) {
            fetchComparablePlayers(value);
        } else if (value.length > 3) {
            const constructedPlayerId = value.replace(/\s/g, "_").toLowerCase();
            const filtered = comparablePlayerCache.filter((option) =>
                option.player_id.includes(constructedPlayerId),
            );
            setComparablePlayerOptions(sortPlayerOptions(filtered));
        } else {
            setComparablePlayerOptions([]);
        }
    };

    const loadReports = useCallback(async () => {
        if (!playerId) return;
        setLoading(true);
        setLoadError("");
        try {
            const token = await ensureAccessToken();
            const res = token
                ? await authAxios.get(`/scouting_reports/player/${playerId}`, {
                    headers: authRequestHeaders(token),
                })
                : await publicApi.get(`/scouting_reports/player/${playerId}`);
            setReports(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.warn("[scouting-reports] load failed", err.response?.status, err.response?.data);
            setReports([]);
            setLoadError("Could not load scouting reports. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [playerId, ensureAccessToken]);

    useEffect(() => {
        if (!isActive || !playerId) return;
        loadReports();
    }, [isActive, playerId, loadReports, authLoading]);

    useEffect(() => {
        onReportCountChange?.(reports.length);
    }, [reports, onReportCountChange]);

    useEffect(() => {
        if (!editingId) {
            setForm(emptyForm(playerId));
            setComparablePlayerInput("");
            setComparablePlayerOptions([]);
            setComparablePlayerCache([]);
        }
    }, [playerId, editingId]);

    const updateForm = (patch) => {
        setForm((prev) => ({ ...prev, ...patch }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user || submitting || !playerId) return;

        const payload = buildPayload(form);
        const hasContent =
            payload.summary
            || payload.strength
            || payload.weakness
            || payload.floor
            || payload.ceiling
            || ALL_TRAIT_KEYS.some((key) => payload[key] != null);

        if (!hasContent) {
            setSubmitError("Add at least a summary, note, or attribute score before saving.");
            return;
        }

        if (!projectionTiersValid(payload.floor, payload.ceiling)) {
            setSubmitError("Floor cannot be higher than ceiling.");
            return;
        }

        setSubmitting(true);
        setSubmitError("");
        try {
            const token = await ensureAccessToken();
            if (!token) {
                setSubmitError("Sign in to save a scouting report.");
                return;
            }

            const headers = authRequestHeaders(token);
            const res = editingId
                ? await authAxios.put(`/scouting_reports/${editingId}`, payload, { headers })
                : await authAxios.post("/scouting_reports", payload, { headers });

            const saved = res.data;
            setReports((prev) => {
                if (editingId) {
                    return prev.map((r) =>
                        r.scouting_report_id === editingId ? saved : r,
                    );
                }
                return [saved, ...prev];
            });
            resetForm();
            setActiveView(SCOUTING_VIEWS.YOURS);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setSubmitError(
                typeof detail === "string"
                    ? detail
                    : "Failed to save scouting report. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (report) => {
        setActiveView(SCOUTING_VIEWS.CREATE);
        setEditingId(report.scouting_report_id);
        const nextForm = formFromReport(report);
        setForm(nextForm);
        setComparablePlayerInput(nextForm.comparable_player || "");
        setComparablePlayerOptions([]);
        setComparablePlayerCache([]);
        setSubmitError("");
    };

    const handleDelete = async (reportId) => {
        if (!user || actionId) return;
        setActionId(reportId);
        try {
            const token = await ensureAccessToken();
            if (!token) return;
            await authAxios.delete(`/scouting_reports/${reportId}`, {
                headers: authRequestHeaders(token),
            });
            setReports((prev) => prev.filter((r) => r.scouting_report_id !== reportId));
            if (editingId === reportId) {
                resetForm();
            }
        } catch (err) {
            console.warn("[scouting-reports] delete failed", err.response?.status, err.response?.data);
        } finally {
            setActionId(null);
        }
    };

    return (
        <Box className="scouting-report-tab-panel" hidden={!isActive}>
            <Dialog
                open={Boolean(pendingDeleteReport)}
                onClose={() => setPendingDeleteReport(null)}
                aria-labelledby="scouting-report-delete-title"
                PaperProps={{
                    sx: {
                        backgroundColor: "var(--bg-surface-2)",
                        border: "1px solid var(--border-default)",
                        borderRadius: "12px",
                        color: "var(--text-primary)",
                    },
                }}
            >
                <DialogTitle id="scouting-report-delete-title" sx={{ fontWeight: 800 }}>
                    Delete scouting report?
                </DialogTitle>
                <DialogContent sx={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                    This action is permanent and cannot be undone.
                </DialogContent>
                <DialogActions sx={{ padding: "0.85rem 1.1rem 1rem" }}>
                    <Button
                        type="button"
                        variant="text"
                        onClick={() => setPendingDeleteReport(null)}
                        disabled={Boolean(actionId)}
                        sx={{ textTransform: "none", color: "var(--text-secondary)" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        disableElevation
                        onClick={async () => {
                            const id = pendingDeleteReport?.scouting_report_id;
                            setPendingDeleteReport(null);
                            if (!id) return;
                            await handleDelete(id);
                        }}
                        disabled={Boolean(actionId)}
                        sx={{
                            textTransform: "none",
                            borderRadius: "999px",
                            background: "rgba(248, 113, 113, 0.9)",
                            color: "#0b0d10",
                            "&:hover": { background: "rgba(248, 113, 113, 1)" },
                        }}
                    >
                        Delete permanently
                    </Button>
                </DialogActions>
            </Dialog>

            <Box className="scouting-report-toolbar">
                <Box className="scouting-report-section-header">
                    <AssignmentOutlinedIcon className="scouting-report-section-icon" aria-hidden />
                    <Box>
                        <Typography component="h3" className="scouting-report-section-title">
                            Scouting reports
                        </Typography>
                    </Box>
                </Box>
                <ToggleButtonGroup
                    className="scouting-report-view-toggle"
                    value={activeView}
                    exclusive
                    size="small"
                    onChange={(_, next) => {
                        if (next) setActiveView(next);
                    }}
                    aria-label="Scouting report view"
                    sx={scoutingViewToggleSx}
                >
                    <ToggleButton value={SCOUTING_VIEWS.PUBLIC} aria-label="Public scouting reports">
                        <VisibilityOutlinedIcon />
                        Public
                    </ToggleButton>
                    <ToggleButton value={SCOUTING_VIEWS.YOURS} aria-label="Your scouting reports">
                        <PersonOutlinedIcon />
                        Yours
                    </ToggleButton>
                    <ToggleButton value={SCOUTING_VIEWS.CREATE} aria-label="Create scouting report">
                        <CreateOutlinedIcon />
                        Create
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box className="scouting-report-body">
                {activeView === SCOUTING_VIEWS.CREATE && (
                    <Box className="scouting-report-compose">
                        {user ? (
                    <Box component="form" onSubmit={handleSubmit} className="scouting-report-form">
                        <Box className="scouting-report-form-section">
                            <Typography className="scouting-report-form-section-label" variant="caption">
                                Details
                            </Typography>
                            <Box className="scouting-report-form-row scouting-report-form-row--meta">
                                <FormControl
                                    size="small"
                                    className="scouting-report-field scouting-report-field--compact scouting-report-field--select"
                                    disabled={submitting}
                                >
                                    <InputLabel id="scouting-report-confidence-label" shrink>
                                        Confidence
                                    </InputLabel>
                                    <Select
                                        labelId="scouting-report-confidence-label"
                                        label="Confidence"
                                        value={form.report_confidence_level}
                                        onChange={(e) => updateForm({ report_confidence_level: e.target.value })}
                                        displayEmpty
                                        inputProps={{ "aria-label": "Confidence" }}
                                        InputLabelProps={selectLabelProps}
                                        sx={selectFieldSx}
                                        renderValue={(value) => renderSelectValue(value, formatConfidenceLabel)}
                                        MenuProps={selectMenuProps}
                                    >
                                        <MenuItem value="">
                                            Not set
                                        </MenuItem>
                                        {CONFIDENCE_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl
                                    size="small"
                                    className="scouting-report-field scouting-report-field--compact scouting-report-field--select"
                                    disabled={submitting}
                                >
                                    <InputLabel id="scouting-report-nhl-eta-label" shrink>
                                        NHL ETA
                                    </InputLabel>
                                    <Select
                                        labelId="scouting-report-nhl-eta-label"
                                        label="NHL ETA"
                                        value={form.nhl_eta}
                                        onChange={(e) => updateForm({ nhl_eta: e.target.value })}
                                        displayEmpty
                                        InputLabelProps={selectLabelProps}
                                        sx={selectFieldSx}
                                        renderValue={(value) => renderSelectValue(value)}
                                        MenuProps={selectMenuProps}
                                    >
                                        <MenuItem value="">
                                            Not set
                                        </MenuItem>
                                        {nhlEtaOptions(form.nhl_eta).map((season) => (
                                            <MenuItem key={season} value={season}>
                                                {season}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl
                                    size="small"
                                    className="scouting-report-field scouting-report-field--compact scouting-report-field--select"
                                    disabled={submitting}
                                >
                                    <InputLabel id="scouting-report-floor-label" shrink>
                                        Floor
                                    </InputLabel>
                                    <Select
                                        labelId="scouting-report-floor-label"
                                        label="Floor"
                                        value={form.floor}
                                        InputLabelProps={selectLabelProps}
                                        sx={selectFieldSx}
                                        renderValue={(value) => renderSelectValue(value)}
                                        MenuProps={selectMenuProps}
                                        onChange={(e) => {
                                            const floor = e.target.value;
                                            const updates = { floor };
                                            const nextFloorIdx = projectionTierIndex(floor);
                                            const currentCeilingIdx = projectionTierIndex(form.ceiling);
                                            if (
                                                floor
                                                && form.ceiling
                                                && nextFloorIdx !== -1
                                                && currentCeilingIdx !== -1
                                                && nextFloorIdx < currentCeilingIdx
                                            ) {
                                                updates.ceiling = floor;
                                            }
                                            updateForm(updates);
                                        }}
                                        displayEmpty
                                    >
                                        <MenuItem value="">
                                            Not set
                                        </MenuItem>
                                        {floorOptions.map((tier) => (
                                            <MenuItem key={tier} value={tier}>
                                                {tier}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl
                                    size="small"
                                    className="scouting-report-field scouting-report-field--compact scouting-report-field--select"
                                    disabled={submitting}
                                >
                                    <InputLabel id="scouting-report-ceiling-label" shrink>
                                        Ceiling
                                    </InputLabel>
                                    <Select
                                        labelId="scouting-report-ceiling-label"
                                        label="Ceiling"
                                        value={form.ceiling}
                                        InputLabelProps={selectLabelProps}
                                        sx={selectFieldSx}
                                        renderValue={(value) => renderSelectValue(value)}
                                        MenuProps={selectMenuProps}
                                        onChange={(e) => {
                                            const ceiling = e.target.value;
                                            const updates = { ceiling };
                                            const nextCeilingIdx = projectionTierIndex(ceiling);
                                            const currentFloorIdx = projectionTierIndex(form.floor);
                                            if (
                                                ceiling
                                                && form.floor
                                                && nextCeilingIdx !== -1
                                                && currentFloorIdx !== -1
                                                && currentFloorIdx < nextCeilingIdx
                                            ) {
                                                updates.floor = ceiling;
                                            }
                                            updateForm(updates);
                                        }}
                                        displayEmpty
                                    >
                                        <MenuItem value="">
                                            Not set
                                        </MenuItem>
                                        {ceilingOptions.map((tier) => (
                                            <MenuItem key={tier} value={tier}>
                                                {tier}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        <Box className="scouting-report-form-section">
                            <Typography className="scouting-report-form-section-label" variant="caption">
                                Attributes (0–5)
                            </Typography>
                            <Box
                                className={[
                                    "scouting-report-traits",
                                    isGoalie(playerData)
                                        ? "scouting-report-traits--goalie"
                                        : "scouting-report-traits--skater",
                                ].join(" ")}
                            >
                                {traitFields.map(({ key, label }) => (
                                    <TextField
                                        key={key}
                                        label={label}
                                        type="number"
                                        value={form[key]}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === "") {
                                                updateForm({ [key]: "" });
                                                return;
                                            }
                                            if (!/^\d+$/.test(v)) return;
                                            const n = Number(v);
                                            if (n > TRAIT_SCORE_MAX) return;
                                            updateForm({ [key]: v });
                                        }}
                                        inputProps={{
                                            min: TRAIT_SCORE_MIN,
                                            max: TRAIT_SCORE_MAX,
                                            step: 1,
                                        }}
                                        InputLabelProps={traitLabelProps}
                                        size="small"
                                        className="scouting-report-field scouting-report-field--trait"
                                        sx={traitFieldSx}
                                        disabled={submitting}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <Box className="scouting-report-form-section">
                            <Typography className="scouting-report-form-section-label" variant="caption">
                                Analysis
                            </Typography>
                            <Box className="scouting-report-form-row scouting-report-form-row--analysis-meta">
                                <TextField
                                    label="Game(s) watched"
                                    value={form.game_watched}
                                    onChange={(e) => updateForm({ game_watched: e.target.value })}
                                    className="search-bar scouting-report-analysis-search-field"
                                    placeholder="game · date · league"
                                    disabled={submitting}
                                    fullWidth
                                />
                                <Autocomplete
                                    className="autocomplete-search-bar scouting-report-analysis-search"
                                    forcePopupIcon={false}
                                    options={comparablePlayerOptions}
                                    getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                                    onChange={(_, option) => {
                                        setComparablePlayerOptions([]);
                                        if (option) {
                                            const name = playerDisplayName(option);
                                            setComparablePlayerInput(name);
                                            updateForm({ comparable_player: name });
                                        }
                                    }}
                                    disabled={submitting}
                                    renderInput={(params) => (
                                        <TextField
                                            className="search-bar scouting-report-analysis-search-field"
                                            {...params}
                                            label="Comparable player"
                                            value={comparablePlayerInput}
                                            onChange={handleComparablePlayerSearchChange}
                                        />
                                    )}
                                />
                            </Box>
                            <Box className="scouting-report-form-row scouting-report-form-row--notes">
                                <TextField
                                    label="Strengths"
                                    value={form.strength}
                                    onChange={(e) => updateForm({ strength: e.target.value })}
                                    size="small"
                                    multiline
                                    minRows={2}
                                    className="scouting-report-field"
                                    disabled={submitting}
                                    fullWidth
                                />
                                <TextField
                                    label="Weaknesses"
                                    value={form.weakness}
                                    onChange={(e) => updateForm({ weakness: e.target.value })}
                                    size="small"
                                    multiline
                                    minRows={2}
                                    className="scouting-report-field"
                                    disabled={submitting}
                                    fullWidth
                                />
                            </Box>
                            <TextField
                                label="Summary"
                                value={form.summary}
                                onChange={(e) => updateForm({ summary: e.target.value })}
                                size="small"
                                multiline
                                minRows={2}
                                className="scouting-report-field"
                                disabled={submitting}
                                fullWidth
                            />
                        </Box>

                        <Box className="scouting-report-form-footer">
                            <Box className="scouting-report-visibility-picker">
                                <Typography className="scouting-report-visibility-picker-label" variant="caption">
                                    Who can see this report?
                                </Typography>
                                <Box
                                    className="scouting-report-visibility-options"
                                    role="group"
                                    aria-label="Report visibility"
                                >
                                    <button
                                        type="button"
                                        className={[
                                            "scouting-report-visibility-option",
                                            "scouting-report-visibility-option--private",
                                            !form.public ? "scouting-report-visibility-option--active" : "",
                                        ].filter(Boolean).join(" ")}
                                        onClick={() => updateForm({ public: false })}
                                        disabled={submitting}
                                        aria-pressed={!form.public}
                                    >
                                        <VisibilityOffOutlinedIcon className="scouting-report-visibility-option-icon" aria-hidden />
                                        <span className="scouting-report-visibility-option-copy">
                                            <span className="scouting-report-visibility-option-title">Private</span>
                                            <span className="scouting-report-visibility-option-desc">
                                                Only you
                                            </span>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className={[
                                            "scouting-report-visibility-option",
                                            "scouting-report-visibility-option--public",
                                            form.public ? "scouting-report-visibility-option--active" : "",
                                        ].filter(Boolean).join(" ")}
                                        onClick={() => updateForm({ public: true })}
                                        disabled={submitting}
                                        aria-pressed={form.public}
                                    >
                                        <VisibilityOutlinedIcon className="scouting-report-visibility-option-icon" aria-hidden />
                                        <span className="scouting-report-visibility-option-copy">
                                            <span className="scouting-report-visibility-option-title">Public</span>
                                            <span className="scouting-report-visibility-option-desc">
                                                Everyone
                                            </span>
                                        </span>
                                    </button>
                                </Box>
                            </Box>
                            <Box className="scouting-report-form-actions">
                                {editingId && (
                                    <Button
                                        type="button"
                                        variant="text"
                                        onClick={resetForm}
                                        disabled={submitting}
                                        sx={{ textTransform: "none" }}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    type="submit"
                                    size="small"
                                    className="scouting-report-submit-btn"
                                    disabled={submitting}
                                    disableElevation
                                    endIcon={
                                        submitting ? (
                                            <CircularProgress size={14} color="inherit" />
                                        ) : (
                                            <SendRoundedIcon sx={{ fontSize: "0.9rem" }} />
                                        )
                                    }
                                >
                                    {submitting
                                        ? "Saving…"
                                        : editingId
                                            ? "Update report"
                                            : "Post report"}
                                </Button>
                            </Box>
                        </Box>

                        {submitError && (
                            <Typography className="scouting-report-error" variant="body2">
                                {submitError}
                            </Typography>
                        )}
                    </Box>
                        ) : (
                            <Box className="scouting-report-sign-in-card">
                                <AssignmentOutlinedIcon className="scouting-report-sign-in-icon" aria-hidden />
                                <Typography className="scouting-report-sign-in-text" variant="body2">
                                    <Link to="/login" className="scouting-report-sign-in-link">Sign in</Link>
                                    {" "}to write your own scouting report.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {activeView !== SCOUTING_VIEWS.CREATE && (
                    <Box className="scouting-report-list-scroll">
                        {!user && activeView === SCOUTING_VIEWS.YOURS && (
                            <Box className="scouting-report-sign-in-card">
                                <AssignmentOutlinedIcon className="scouting-report-sign-in-icon" aria-hidden />
                                <Typography className="scouting-report-sign-in-text" variant="body2">
                                    <Link to="/login" className="scouting-report-sign-in-link">Sign in</Link>
                                    {" "}to view your scouting reports.
                                </Typography>
                            </Box>
                        )}

                        {(user || activeView === SCOUTING_VIEWS.PUBLIC) && (
                            <>
                {loading && (
                    <Box className="scouting-report-loading">
                        <CircularProgress size={28} sx={{ color: "var(--brand-strong)" }} />
                        <Typography variant="body2">Loading scouting reports…</Typography>
                    </Box>
                )}

                {!loading && loadError && (
                    <Box className="scouting-report-empty-state scouting-report-empty-state--error">
                        <Typography variant="body1">{loadError}</Typography>
                        <Button className="scouting-report-retry-btn" onClick={loadReports} disableElevation>
                            Try again
                        </Button>
                    </Box>
                )}

                {!loading && !loadError && listReports.length === 0 && (
                    <Box className="scouting-report-empty-state">
                        <AssignmentOutlinedIcon className="scouting-report-empty-icon" aria-hidden />
                        <Typography className="scouting-report-empty-title" variant="subtitle1">
                            {listEmptyState.title}
                        </Typography>
                        <Typography className="scouting-report-empty-caption" variant="body2">
                            {listEmptyState.caption}
                        </Typography>
                    </Box>
                )}

                {!loading && !loadError && listReports.length > 0 && (
                    <Box className="scouting-report-list">
                        {listReports.map((report) => {
                            const author = report.user;
                            const isOwner = report.is_owner;
                            const isBusy = actionId === report.scouting_report_id;
                            const traits = traitFields.filter(
                                ({ key }) => report[key] != null && report[key] !== "",
                            );
                            const hasOutlook =
                                report.floor?.trim()
                                || report.ceiling?.trim()
                                || report.nhl_eta?.trim()
                                || report.game_watched?.trim()
                                || report.comparable_player?.trim()
                                || report.report_confidence_level?.trim();

                            return (
                                <article
                                    key={report.scouting_report_id}
                                    className={`scouting-report-card${isOwner ? " scouting-report-card--own" : ""}`}
                                >
                                    <Box className="scouting-report-card-header">
                                        <Box className="scouting-report-card-author">
                                            <Avatar
                                                src={author?.picture_url || undefined}
                                                alt={userDisplayName(author)}
                                                className="scouting-report-card-avatar"
                                            >
                                                {userInitial(author)}
                                            </Avatar>
                                            <Box>
                                                <Box className="scouting-report-card-meta">
                                                    <Link
                                                        to={author?.id ? `/profile/${author.id}` : "#"}
                                                        className="scouting-report-author-name"
                                                    >
                                                        {userDisplayName(author)}
                                                    </Link>
                                                    {isOwner && (
                                                        <span className="scouting-report-you-badge">You</span>
                                                    )}
                                                    <span className="scouting-report-card-date">
                                                        {formatReportDate(report.creation_date)}
                                                    </span>
                                                </Box>
                                                <Box className="scouting-report-card-badges">
                                                    {report.public ? (
                                                        <span className="scouting-report-visibility scouting-report-visibility--public">
                                                            <span className="scouting-report-badge-key">Visibility</span>
                                                            <span className="scouting-report-badge-sep" aria-hidden>:</span>
                                                            <VisibilityOutlinedIcon fontSize="inherit" />
                                                            <span className="scouting-report-badge-value">Public</span>
                                                        </span>
                                                    ) : (
                                                        <span className="scouting-report-visibility scouting-report-visibility--private">
                                                            <span className="scouting-report-badge-key">Visibility</span>
                                                            <span className="scouting-report-badge-sep" aria-hidden>:</span>
                                                            <VisibilityOffOutlinedIcon fontSize="inherit" />
                                                            <span className="scouting-report-badge-value">Private</span>
                                                        </span>
                                                    )}
                                                    {report.report_confidence_level && (
                                                        <span className="scouting-report-confidence">
                                                            <span className="scouting-report-badge-key">Confidence</span>
                                                            <span className="scouting-report-badge-sep" aria-hidden>:</span>
                                                            <span className="scouting-report-badge-value">
                                                                {formatConfidenceLabel(report.report_confidence_level)}
                                                            </span>
                                                        </span>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                        {isOwner && (
                                            <Box className="scouting-report-card-actions">
                                                <button
                                                    type="button"
                                                    className="scouting-report-action-btn"
                                                    onClick={() => handleEdit(report)}
                                                    disabled={isBusy || submitting}
                                                    aria-label="Edit report"
                                                >
                                                    <EditOutlinedIcon sx={{ fontSize: "0.95rem" }} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="scouting-report-action-btn scouting-report-action-btn--delete"
                                                    onClick={() => setPendingDeleteReport(report)}
                                                    disabled={isBusy || submitting}
                                                    aria-label="Delete report"
                                                >
                                                    {isBusy ? (
                                                        <CircularProgress size={12} />
                                                    ) : (
                                                        <DeleteOutlineRoundedIcon sx={{ fontSize: "0.95rem" }} />
                                                    )}
                                                </button>
                                            </Box>
                                        )}
                                    </Box>

                                    {hasOutlook && (
                                        <Box className="scouting-report-outlook">
                                            {report.nhl_eta && (
                                                <div className="scouting-report-outlook-item">
                                                    <span className="scouting-report-outlook-label">NHL ETA</span>
                                                    <span className="scouting-report-outlook-value">{report.nhl_eta}</span>
                                                </div>
                                            )}
                                            {report.game_watched && (
                                                <div className="scouting-report-outlook-item">
                                                    <span className="scouting-report-outlook-label">Game(s) watched</span>
                                                    <span className="scouting-report-outlook-value">{report.game_watched}</span>
                                                </div>
                                            )}
                                            {report.comparable_player && (
                                                <div className="scouting-report-outlook-item">
                                                    <span className="scouting-report-outlook-label">Comparable player</span>
                                                    <span className="scouting-report-outlook-value">{report.comparable_player}</span>
                                                </div>
                                            )}
                                            {report.floor && (
                                                <div className="scouting-report-outlook-item">
                                                    <span className="scouting-report-outlook-label">Floor</span>
                                                    <span className="scouting-report-outlook-value">{report.floor}</span>
                                                </div>
                                            )}
                                            {report.ceiling && (
                                                <div className="scouting-report-outlook-item">
                                                    <span className="scouting-report-outlook-label">Ceiling</span>
                                                    <span className="scouting-report-outlook-value">{report.ceiling}</span>
                                                </div>
                                            )}
                                        </Box>
                                    )}

                                    {report.strength && (
                                        <Box className="scouting-report-text-block">
                                            <span className="scouting-report-text-label">Strengths</span>
                                            <p className="scouting-report-text-body">{report.strength}</p>
                                        </Box>
                                    )}
                                    {report.weakness && (
                                        <Box className="scouting-report-text-block">
                                            <span className="scouting-report-text-label">Weaknesses</span>
                                            <p className="scouting-report-text-body">{report.weakness}</p>
                                        </Box>
                                    )}
                                    {report.summary && (
                                        <Box className="scouting-report-text-block">
                                            <span className="scouting-report-text-label">Summary</span>
                                            <p className="scouting-report-text-body">{report.summary}</p>
                                        </Box>
                                    )}

                                    {traits.length > 0 && (
                                        <Box className="scouting-report-card-attributes">
                                            <span className="scouting-report-subsection-title">Attributes</span>
                                            <Box className="scouting-report-card-traits">
                                                {traits.map(({ key, label }) => (
                                                    <div key={key} className="scouting-report-trait" title={label}>
                                                        <span className="scouting-report-trait-label">{label}</span>
                                                        <span className="scouting-report-trait-bar-wrap" aria-hidden>
                                                            <span
                                                                className={[
                                                                    "scouting-report-trait-bar",
                                                                    traitColorClass(report[key]),
                                                                ].join(" ")}
                                                                style={{ width: `${(Number(report[key]) / 5) * 100}%` }}
                                                            />
                                                        </span>
                                                        <span className="scouting-report-trait-score">
                                                            {report[key]}/5
                                                        </span>
                                                    </div>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </article>
                            );
                        })}
                    </Box>
                )}
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default PlayerScoutingReportTab;
